import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { createUseStyles, useTheme, Text, Icon, Button } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';

import ConditionsContext from './ConditionsContext';
import { getEmptyGroup, getConditionsString, validateConditions } from './utils.js';
import Group from './Group';
import OperatorSwitch from './OperatorSwitch';

import styles from './styles/root';
const useStyles = createUseStyles(styles, 'Conditions');

const Conditions = ({ conditions, fields, isReadOnly, onChange, listMapFields = {}, options }) => {
    const { t, locale } = useWeb5Context();
    const classes = useStyles();
    const theme = useTheme();
    const [indexCurrentGroup, setIndexCurrentGroup] = useState(0);

    const conditionStr = useMemo(
        () => getConditionsString(conditions, locale, listMapFields),
        [conditions, locale, listMapFields],
    );

    const validConditions = useMemo(() => validateConditions(conditions), [conditions]);

    const handleGroupChange = useCallback(
        (index, group) => {
            const newGroups = [...conditions.groups];
            newGroups[index] = group;
            onChange({
                operator: conditions.operator,
                groups: newGroups,
            });
        },
        [conditions.groups, conditions.operator, onChange],
    );

    const handleGroupDelete = useCallback(
        (index) => {
            const newGroups = [...conditions.groups];
            newGroups.splice(index, 1);
            onChange({
                operator: conditions.operator,
                groups: newGroups,
            });
        },
        [conditions.groups, conditions.operator, onChange],
    );

    const handleAddGroup = useCallback(() => {
        const newGroups = [...conditions.groups, getEmptyGroup()];
        setIndexCurrentGroup(newGroups.length - 1);
        onChange({
            operator: conditions.operator,
            groups: newGroups,
        });
    }, [conditions.groups, conditions.operator, onChange]);

    const handleLogicOperatorChange = useCallback(
        (val) => {
            onChange({
                operator: val,
                groups: conditions.groups,
            });
        },
        [conditions.groups, onChange],
    );

    const renderGroup = useCallback(
        (group, index) => (
            <Group
                key={`group-${index}`}
                group={group}
                index={index}
                onChange={!isReadOnly ? handleGroupChange : undefined}
                onDelete={
                    !isReadOnly && conditions?.groups?.length > 1 ? handleGroupDelete : undefined
                }
                expanded={index === indexCurrentGroup}
                toggleExpanded={setIndexCurrentGroup}
                listMapFields={listMapFields}
                options={options}
            />
        ),
        [
            listMapFields,
            isReadOnly,
            handleGroupChange,
            conditions,
            handleGroupDelete,
            indexCurrentGroup,
            options,
        ],
    );

    const renderGroupSeparator = useCallback(
        (prev, current, index) => [
            prev,
            <div key={`separator-${index}`} className={classes.groupSeparator}>
                {index === 1 && (
                    <OperatorSwitch
                        value={conditions.operator}
                        onChange={!isReadOnly ? handleLogicOperatorChange : undefined}
                    />
                )}
            </div>,
            current,
        ],
        [classes.groupSeparator, conditions.operator, handleLogicOperatorChange, isReadOnly],
    );

    const renderedGroups = useMemo(
        () => conditions.groups && conditions.groups.map(renderGroup).reduce(renderGroupSeparator),
        [conditions.groups, renderGroup, renderGroupSeparator],
    );

    const context = useMemo(
        () => ({
            fields,
            isReadOnly,
        }),
        [fields, isReadOnly],
    );

    return (
        <div className={classes.root}>
            <ConditionsContext.Provider value={context}>
                {renderedGroups}
                {!isReadOnly && (
                    <div className={classes.action}>
                        <Button
                            onClick={handleAddGroup}
                            type="terciary"
                            className="conditions__add-condition-group-button"
                        >
                            {t('cfm_action_add_group')}
                        </Button>
                    </div>
                )}
                {conditionStr && conditionStr.length > 0 && (
                    <div className={classes.conditionStringContainer}>
                        {validConditions ? (
                            <Icon
                                name="thickEnabledFilled"
                                color={theme.colors.semantic.positive500}
                            />
                        ) : (
                            <Icon name="thickDisabled" color={theme.colors.semantic.negative500} />
                        )}
                        <Text className={classes.conditionString}>{conditionStr}</Text>
                    </div>
                )}
            </ConditionsContext.Provider>
        </div>
    );
};

Conditions.propTypes = {
    conditions: PropTypes.shape({}).isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
            type: PropTypes.oneOf([
                'bit',
                'text',
                'datetime',
                'decimal',
                'int',
                'list',
                'multivalue',
                'currency',
                'percentage',
            ]).isRequired,
            table: PropTypes.string,
            conditions: PropTypes.arrayOf(PropTypes.string),
        }),
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
    listMapFields: PropTypes.shape({}),
};

export {
    getEmptyConditions,
    mapToJsonLogic,
    mapConditions,
    getConditionsString,
    mapFieldsValues,
    validateConditions,
    transformDataConditions,
} from './utils';
export default memo(Conditions);
